import React, { useEffect } from 'react';
import { FaShieldAlt } from 'react-icons/fa';

const Privacy = () => {
  useEffect(() => {
    document.title = "Privacy Policy - SastaMastaRide";
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <FaShieldAlt className="text-blue-600 text-5xl mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-lg max-w-none text-gray-700">
          <div className="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-600">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> This website is operated by <strong>SastaMastaRide</strong>, managed by <strong>RKTECH Solutions</strong>.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introduction</h2>
            <p className="mb-4">
              SastaMastaRide ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains 
              how we collect, use, disclose, and safeguard your information when you use our ridesharing platform 
              ("Platform", "Service").
            </p>
            <p>
              By using our Platform, you consent to the data practices described in this Privacy Policy. If you do 
              not agree with the practices described, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">2.1 Personal Information</h3>
            <p className="mb-4">We collect information that you provide directly to us:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li><strong>Account Information:</strong> Name, email address, phone number, date of birth</li>
              <li><strong>Profile Information:</strong> Profile photo, bio, preferences</li>
              <li><strong>Verification Documents:</strong> For riders - driving license, vehicle registration, identity proof</li>
              <li><strong>Payment Information:</strong> Payment method details (processed securely through payment gateways)</li>
              <li><strong>Location Data:</strong> Trip origin, destination, and real-time location during active trips</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">2.2 Automatically Collected Information</h3>
            <p className="mb-4">We automatically collect certain information when you use our Platform:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on Platform</li>
              <li><strong>Location Data:</strong> GPS coordinates, location history (with your permission)</li>
              <li><strong>Cookies and Tracking:</strong> Cookies, web beacons, and similar tracking technologies</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">2.3 Information from Third Parties</h3>
            <p>
              We may receive information from third-party services such as payment processors, authentication 
              services, and analytics providers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the collected information for the following purposes:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li><strong>Service Provision:</strong> To provide, maintain, and improve our ridesharing services</li>
              <li><strong>User Matching:</strong> To connect riders and passengers for shared rides</li>
              <li><strong>Verification:</strong> To verify rider credentials and ensure platform safety</li>
              <li><strong>Payment Processing:</strong> To process payments and manage financial transactions</li>
              <li><strong>Communication:</strong> To send trip updates, notifications, and customer support messages</li>
              <li><strong>Safety:</strong> To ensure user safety through real-time tracking and emergency features</li>
              <li><strong>Analytics:</strong> To analyze usage patterns and improve user experience</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms</li>
              <li><strong>Marketing:</strong> To send promotional communications (with your consent)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Information Sharing and Disclosure</h2>
            <p className="mb-4">We may share your information in the following circumstances:</p>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-2">4.1 With Other Users</h3>
            <p className="mb-4">
              When you book or offer a ride, we share necessary information (name, profile photo, ratings) with 
              the other party to facilitate the ride.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">4.2 Service Providers</h3>
            <p className="mb-4">
              We share information with third-party service providers who perform services on our behalf, such as 
              payment processing, data analytics, cloud storage, and customer support.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">4.3 Legal Requirements</h3>
            <p className="mb-4">
              We may disclose information if required by law, court order, or government regulation, or to protect 
              the rights, property, or safety of SastaMastaRide, our users, or others.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2">4.4 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the 
              acquiring entity.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Data Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational security measures to protect your information:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Encryption of sensitive data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and assessments</li>
              <li>Limited access to personal information on a need-to-know basis</li>
              <li>Secure payment processing through certified payment gateways</li>
            </ul>
            <p>
              However, no method of transmission over the internet or electronic storage is 100% secure. While we 
              strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Your Privacy Rights</h2>
            <p className="mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Objection:</strong> Object to processing of your information for certain purposes</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent for data processing where applicable</li>
            </ul>
            <p>
              To exercise these rights, contact us at privacy@sastamastaride.com. We will respond to your request 
              within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to enhance your experience, analyze usage, and 
              provide personalized content. You can control cookies through your browser settings, but disabling 
              cookies may limit certain Platform features.
            </p>
            <p>
              Types of cookies we use:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li><strong>Essential Cookies:</strong> Required for Platform functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Platform</li>
              <li><strong>Preference Cookies:</strong> Remember your preferences and settings</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Location Data</h2>
            <p className="mb-4">
              We collect location data to provide core features like trip matching, real-time tracking, and route 
              optimization. Location data is:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Collected only with your explicit permission</li>
              <li>Used only for Platform services</li>
              <li>Shared only with relevant parties (rider/passenger) during active trips</li>
              <li>Stored securely and deleted according to our retention policy</li>
            </ul>
            <p>
              You can disable location services through your device settings, but this may limit Platform functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Data Retention</h2>
            <p className="mb-4">
              We retain your personal information for as long as necessary to provide our services and fulfill 
              the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.
            </p>
            <p>
              When you delete your account, we will delete or anonymize your personal information, except where 
              we are required to retain it for legal, regulatory, or business purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Children's Privacy</h2>
            <p>
              Our Platform is not intended for users under 18 years of age. We do not knowingly collect personal 
              information from children. If we become aware that we have collected information from a child under 
              18, we will take steps to delete such information immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of 
              residence. We ensure appropriate safeguards are in place to protect your information in accordance 
              with this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use 
              of the Platform after changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">13. Contact Us</h2>
            <p className="mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p><strong>Email:</strong> privacy@sastamastaride.com</p>
              <p><strong>Data Protection Officer:</strong> dpo@sastamastaride.com</p>
              <p><strong>Address:</strong> SastaMastaRide, Managed by RKTECH Solutions, India</p>
              <p><strong>Phone:</strong> +91-1800-XXX-XXXX</p>
            </div>
          </section>

          <div className="bg-blue-50 p-6 rounded-lg mt-8">
            <p className="text-gray-700">
              By using SastaMastaRide, you acknowledge that you have read and understood this Privacy Policy and 
              consent to the collection, use, and disclosure of your information as described herein.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;

